import React, { useMemo, useRef, useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./PostRichTextEditor.css";

// Minimal outline smiley (line style) — inherits color via currentColor.
const EmojiGlyph = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
    <line x1="9" y1="9" x2="9.01" y2="9" />
    <line x1="15" y1="9" x2="15.01" y2="9" />
  </svg>
);

// Each editor instance needs its own toolbar container id so Quill wires the
// right buttons when more than one composer is mounted (Add Post + Share Chart).
let editorInstanceCount = 0;

// Lightweight, dependency-free emoji set for the picker popover.
const EMOJIS = [
  "😀", "😂", "🥹", "😍", "😎", "🤔", "😅", "🙃",
  "👍", "👏", "🙌", "💪", "🙏", "🔥", "🚀", "✨",
  "📈", "📉", "💰", "💎", "🐂", "🐻", "🎯", "💡",
  "✅", "❌", "⚠️", "👀", "❤️", "🎉", "🤝", "😱",
];

/**
 * Shared post composer used by Add Post and the Share Chart modal.
 * Same static toolbar on desktop and mobile — B / I / U clustered left, emoji
 * anchored next to them, with a hairline divider. (A contextual mobile bubble
 * was dropped because it collided with the OS Cut/Copy/Paste menu.)
 */
const PostRichTextEditor = ({ value, onChange, placeholder, spacious = false }) => {
  const quillRef = useRef(null);
  const emojiWrapRef = useRef(null);
  const [showEmoji, setShowEmoji] = useState(false);

  const toolbarIdRef = useRef(null);
  if (!toolbarIdRef.current) {
    toolbarIdRef.current = `post-editor-toolbar-${++editorInstanceCount}`;
  }

  // Bind Quill to our custom toolbar container (B/I/U live there; the emoji
  // button is handled in React, outside Quill's format buttons).
  const modules = useMemo(
    () => ({
      toolbar: { container: `#${toolbarIdRef.current}` },
      clipboard: { matchVisual: false },
    }),
    []
  );

  const formats = useMemo(() => ["bold", "italic", "underline"], []);

  // Close the emoji popover on outside click.
  useEffect(() => {
    if (!showEmoji) return;
    const onDocClick = (e) => {
      if (emojiWrapRef.current && !emojiWrapRef.current.contains(e.target)) {
        setShowEmoji(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showEmoji]);

  const insertEmoji = (emoji) => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;
    const range = editor.getSelection(true) || {
      index: editor.getLength(),
      length: 0,
    };
    editor.insertText(range.index, emoji, "user");
    editor.setSelection(range.index + emoji.length, 0);
    setShowEmoji(false);
  };

  return (
    <div className={`post-editor ${spacious ? "post-editor--spacious" : ""}`}>
      <div className="post-editor-bar">
        {/* Quill binds B/I/U to these by className */}
        <div id={toolbarIdRef.current} className="post-editor-toolbar">
          <span className="ql-formats">
            <button type="button" className="ql-bold" />
            <button type="button" className="ql-italic" />
            <button type="button" className="ql-underline" />
          </span>
        </div>
        {/* Emoji grouped next to B/I/U */}
        <div className="post-editor-emoji" ref={emojiWrapRef}>
          <button
            type="button"
            className="peb-emoji"
            aria-label="Insert emoji"
            onClick={() => setShowEmoji((v) => !v)}
          >
            <EmojiGlyph />
          </button>
          {showEmoji && (
            <div className="peb-emoji-popover">
              {EMOJIS.map((emoji) => (
                <button
                  type="button"
                  key={emoji}
                  className="peb-emoji-item"
                  onClick={() => insertEmoji(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </div>
  );
};

export default PostRichTextEditor;
